import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControl, UntypedFormBuilder } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { debounceTime, firstValueFrom, merge, Subscription } from 'rxjs';
import { ComputerService } from 'src/app/computer/computer.service';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { PrinterTarget, Warehouse, WarehouseCell } from 'src/typings';
import { CurrentUserService as UserService } from 'src/app/user/user.service';
import { WarehouseService } from 'src/app/warehouse/warehouse.service';
import { ThermalPrintTargetService } from '../thermal-print-target.service';

@Component({
  selector: 'app-print-cell',
  templateUrl: './print-cell.component.html',
  styleUrls: ['./print-cell.component.scss']
})
export class PrintCellComponent implements OnInit, OnDestroy {
  warehouseId?: number;

  warehouses: Warehouse[] = [];

  cells: WarehouseCell[] = [];

  selectedCell?: WarehouseCell;

  loading = false;

  selectingPrinter = false;

  errorMessage = '';

  private readonly subscriptions = new Subscription();

  private searchVersion = 0;

  formGroup = this.fb.group({
    cell: new FormControl<string>(''),
    warehouseId: new FormControl<number | null>(null)
  });

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly computerService: ComputerService,
    private readonly userService: UserService,
    private readonly notificationService: NotificationService,
    private readonly thermalPrintTargetService: ThermalPrintTargetService,
    @Optional() private readonly dialogRef: MatDialogRef<PrintCellComponent> | null,
    private readonly warehouseService: WarehouseService
  ) {}

  ngOnInit(): void {
    this.formGroup.controls.warehouseId.setValue(
      this.warehouseId ?? this.userService.user?.defaultWarehouse?.id ?? null
    );
    this.subscriptions.add(
      this.warehouseService.getAllList().subscribe({
        next: (warehouses) => (this.warehouses = warehouses),
        error: (error) => this.showError(error, 'Не удалось загрузить склады')
      })
    );
    this.subscriptions.add(
      merge(
        this.formGroup.controls.cell.valueChanges,
        this.formGroup.controls.warehouseId.valueChanges
      )
        .pipe(debounceTime(150))
        .subscribe(() => void this.searchCells())
    );
    void this.searchCells();
  }

  ngOnDestroy(): void {
    this.searchVersion += 1;
    this.subscriptions.unsubscribe();
  }

  get selectedWarehouseId(): number {
    return Number(
      this.formGroup.controls.warehouseId.value ||
        this.warehouseId ||
        this.userService.user?.defaultWarehouse?.id ||
        0
    );
  }

  get cellCode(): string {
    return String(this.formGroup.controls.cell.value || '')
      .trim()
      .replace(/^!+/, '')
      .toUpperCase();
  }

  selectCell(cell: WarehouseCell): void {
    this.selectedCell = cell;
    this.warehouseId = Number(cell.warehouseId);
    this.formGroup.patchValue({ warehouseId: cell.warehouseId, cell: cell.code });
    this.errorMessage = '';
  }

  async searchCells(): Promise<void> {
    const version = ++this.searchVersion;
    const warehouseId = this.selectedWarehouseId;
    const code = this.cellCode;
    if (!warehouseId) return;
    try {
      const cells = await firstValueFrom(
        this.warehouseService.searchCells({
          warehouseId,
          code,
          includeInactive: false
        })
      );
      if (
        version === this.searchVersion &&
        this.selectedWarehouseId === warehouseId &&
        this.cellCode === code
      ) {
        this.cells = cells.filter((cell) => cell.type === 'REAL');
      }
    } catch (error) {
      if (version === this.searchVersion) this.showError(error, 'Не удалось найти ячейки');
    }
  }

  async print(target?: PrinterTarget | null): Promise<void> {
    if (this.loading || this.selectingPrinter) return;
    const code = this.cellCode;
    const warehouseId = this.selectedWarehouseId;
    if (!code || !warehouseId) {
      this.notificationService.warning('Выберите склад и введите адрес ячейки.');
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    let created = false;
    try {
      let cell = this.selectedCell;
      if (!cell || cell.warehouseId !== warehouseId || cell.code !== code) {
        const cells = await firstValueFrom(
          this.warehouseService.searchCells({
            warehouseId,
            code,
            includeInactive: true
          })
        );
        cell = cells.find((candidate) => candidate.code === code);
      }
      if (cell && (!cell.isActive || cell.type !== 'REAL')) {
        throw new Error('Для печати выберите активную физическую ячейку.');
      }
      if (!cell) {
        cell = await firstValueFrom(this.warehouseService.createCell({ warehouseId, code }));
        created = true;
      }
      this.selectCell(cell);
      await this.computerService.printCellLabel({
        cell: cell.code,
        cellCode: cell.code,
        cellId: cell.id,
        quan: 1,
        copies: 1,
        warehouseId: cell.warehouseId,
        barcodeType: 'CODE128',
        barcodeWidth: 2,
        labelProfileMode: 'printer_target',
        target: target
          ? {
              warehouseId: cell.warehouseId,
              agentName: target.agentName,
              agentDeviceId: target.deviceId,
              strategy: 'explicit_agent'
            }
          : { warehouseId: cell.warehouseId, strategy: 'warehouse_online' }
      });
      this.notificationService.notice('Этикетка ячейки отправлена на печать');
      this.dialogRef?.close(true);
    } catch (error) {
      this.showError(error, 'Не удалось напечатать этикетку ячейки', created);
    } finally {
      this.loading = false;
    }
  }

  async printToOtherPrinter(event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.loading || this.selectingPrinter) return;
    this.selectingPrinter = true;
    try {
      const target = await this.thermalPrintTargetService.chooseTarget({
        role: 'label',
        user: this.userService.user,
        warehouseId: this.selectedWarehouseId,
        title: 'Выберите агент печати',
        description: 'Этикетка ячейки будет отправлена выбранному онлайн-агенту.',
        fieldLabel: 'Агент печати',
        submitLabel: 'Печатать'
      });
      this.selectingPrinter = false;
      if (target) await this.print(target);
    } catch (error) {
      this.showError(error, 'Не удалось выбрать агент печати');
    } finally {
      this.selectingPrinter = false;
    }
  }

  private showError(error: any, fallback: string, created = false): void {
    const message = extractApiErrorMessage(error?.error || error, fallback);
    this.errorMessage = created ? `Ячейка создана. Этикетка не отправлена: ${message}` : message;
    this.notificationService.showError(this.errorMessage);
  }
}
